import React from 'react';
import { Link, Outlet } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import LanguageSwitcher from '../components/common/LanguageSwitcher';
import AppFooter from '../components/common/AppFooter';
import AskAiAssistant from '../components/common/AskAiAssistant';

/* ---------- styled ---------- */
const Shell = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--app-bg);
`;

/* Support has no rail and no logo lockup — only this ink top bar, so it gets the
   same ink/hairline chrome treatment as the sidebar layouts. */
const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 20px;
  background: var(--app-sidebar);
  color: var(--text-inverse);
  border-bottom: 1px solid var(--border-inverse);
`;

const Brand = styled.div`
  font-weight: var(--weight-bold);
  font-size: var(--text-md);
  text-transform: uppercase;
  letter-spacing: var(--tracking-none);
  line-height: var(--leading-tight);
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LegalLink = styled.a`
  color: var(--text-inverse, #fff);
  font-size: var(--text-xs);
  text-decoration: none;
  opacity: 0.85;

  &:hover { opacity: 1; text-decoration: underline; }
`;

const LogoutButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.7);
  background: transparent;
  color: var(--text-inverse);
  padding: 6px 14px;
  border-radius: var(--app-radius);
  font-size: var(--text-xs);
  cursor: pointer;
  min-height: 40px;
  transition: all var(--dur-control) var(--ease-control);

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    color: var(--text-inverse);
    box-shadow: none;
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-act);
  }
`;

const Main = styled.main`
  flex: 1;
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  padding: 24px 16px;
`;

/* The console has no sidebar, so the onboarding sections a support user owns
   live as header links. They render inside the AdminLayout (filtered down to
   those three items), which is where the Back-to-console link returns from. */
const SectionNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-left: 20px;
`;

const SectionLink = styled(Link)`
  color: var(--text-inverse, #fff);
  font-size: var(--text-xs);
  text-decoration: none;
  opacity: 0.85;

  &:hover { opacity: 1; text-decoration: underline; }
`;

const SupportLayout = () => {
  const { t } = useTranslation();
  const { logout } = useAuth();

  return (
    <Shell>
      <Header>
        <Brand>Tempus · {t('roles.support')}</Brand>
        <SectionNav>
          <SectionLink to="/admin/customers">{t('nav.admin.customers')}</SectionLink>
          <SectionLink to="/admin/employees">{t('nav.admin.employees')}</SectionLink>
          <SectionLink to="/admin/assignments">{t('nav.admin.assignments')}</SectionLink>
        </SectionNav>
        <Right>
          <AskAiAssistant inverse />
          {/* Support has no sidebar, so its only in-chrome slot is the header
              (the footer link covers the rest). */}
          <LegalLink href="/legal" target="_blank" rel="noopener noreferrer">
            {t('legal.navLabel')}
          </LegalLink>
          <LanguageSwitcher compact />
          <LogoutButton type="button" onClick={logout}>
            {t('common.logout')}
          </LogoutButton>
        </Right>
      </Header>
      <Main>
        <Outlet />
      </Main>
      <AppFooter />
    </Shell>
  );
};

export default SupportLayout;
